import type {ReactNode} from "react";

import {Button} from "@project/design-system-web";
import {useLayoutEffect, useRef, useState} from "react";

import {AppModal} from "./app-modal";

export interface CollectionManagerItem {
  id: string;
  label: string;
}

interface CollectionManagerModalProps {
  isOpen: boolean;
  isPending: boolean;
  isDisabled?: boolean;
  showDone?: boolean;
  canAddItem?: (id: string) => boolean;
  canRemoveItem?: (id: string) => boolean;
  items: readonly CollectionManagerItem[];
  labels: {
    title: string;
    empty: string;
    add: string;
    remove: string;
    done: string;
    alreadyAdded: string;
    updating: string;
    failed: string;
    removeItem: (item: CollectionManagerItem) => string;
  };
  renderPicker: (props: {
    value: string | null;
    onChange: (value: string | null) => void;
    isDisabled: boolean;
  }) => ReactNode;
  onAdd: (id: string) => Promise<boolean>;
  onRemove: (id: string) => Promise<boolean>;
  onClose: () => void;
}

export function CollectionManagerModal({
  isOpen,
  isPending,
  isDisabled = false,
  showDone = true,
  canAddItem,
  canRemoveItem,
  items,
  labels,
  renderPicker,
  onAdd,
  onRemove,
  onClose,
}: CollectionManagerModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [failed, setFailed] = useState(false);
  const operation = useRef(false);
  const alive = useRef(false);
  const current = useRef({
    isOpen,
    isPending,
    isDisabled,
    canAddItem,
    canRemoveItem,
    items,
    onAdd,
    onRemove,
    onClose,
    selectedId,
  });
  useLayoutEffect(() => {
    current.current = {
      isOpen,
      isPending,
      isDisabled,
      canAddItem,
      canRemoveItem,
      items,
      onAdd,
      onRemove,
      onClose,
      selectedId,
    };
  });
  useLayoutEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const busy = isPending || working;
  const alreadyAdded = selectedId !== null && items.some((item) => item.id === selectedId);

  async function run(kind: "add" | "remove", id: string) {
    const frame = current.current;
    if (!alive.current || !frame.isOpen || operation.current || frame.isPending || frame.isDisabled)
      return;
    if (
      kind === "add"
        ? frame.selectedId !== id ||
          frame.items.some((item) => item.id === id) ||
          frame.canAddItem?.(id) === false
        : !frame.items.some((item) => item.id === id) || frame.canRemoveItem?.(id) === false
    )
      return;
    operation.current = true;
    setWorking(true);
    setFailed(false);
    try {
      const succeeded = await (kind === "add" ? frame.onAdd(id) : frame.onRemove(id));
      if (alive.current && succeeded && kind === "add") setSelectedId(null);
    } catch {
      if (alive.current) setFailed(true);
    } finally {
      operation.current = false;
      if (alive.current) setWorking(false);
    }
  }

  function close() {
    const frame = current.current;
    if (alive.current && frame.isOpen && !operation.current && !frame.isPending) frame.onClose();
  }

  return (
    <AppModal.Backdrop isOpen={isOpen} onOpenChange={(open) => !open && close()}>
      <AppModal.Container>
        <AppModal.Dialog className="h-[calc(100dvh-0.5rem)] sm:h-auto sm:max-w-lg">
          {!busy ? <AppModal.CloseTrigger /> : null}
          <AppModal.Header>
            <AppModal.Heading>{labels.title}</AppModal.Heading>
          </AppModal.Header>
          <AppModal.Body>
            <div className="flex flex-col gap-4" aria-busy={busy}>
              {items.length ? (
                <ul className="flex flex-col gap-2">
                  {items.map((item) => (
                    <li
                      key={item.id}
                      className="bg-surface-secondary flex items-center justify-between gap-3 rounded-lg p-2"
                    >
                      <span className="min-w-0 truncate text-sm" title={item.label}>
                        {item.label}
                      </span>
                      <Button
                        aria-label={labels.removeItem(item)}
                        isDisabled={busy || isDisabled || canRemoveItem?.(item.id) === false}
                        size="sm"
                        variant="secondary"
                        onPress={() => void run("remove", item.id)}
                      >
                        {labels.remove}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted text-sm">{labels.empty}</p>
              )}
              {renderPicker({
                value: selectedId,
                onChange: (value) => {
                  const frame = current.current;
                  if (
                    alive.current &&
                    frame.isOpen &&
                    !operation.current &&
                    !frame.isPending &&
                    !frame.isDisabled
                  )
                    setSelectedId(value);
                },
                isDisabled: busy || isDisabled,
              })}
              {alreadyAdded ? (
                <p className="text-muted text-sm" role="status">
                  {labels.alreadyAdded}
                </p>
              ) : null}
              {failed ? <p role="alert">{labels.failed}</p> : null}
              {busy ? <p role="status">{labels.updating}</p> : null}
            </div>
          </AppModal.Body>
          <AppModal.Footer className="justify-end gap-2">
            {showDone ? (
              <Button isDisabled={busy} variant="secondary" onPress={close}>
                {labels.done}
              </Button>
            ) : null}
            <Button
              isDisabled={
                !selectedId ||
                alreadyAdded ||
                busy ||
                isDisabled ||
                (!!selectedId && canAddItem?.(selectedId) === false)
              }
              variant="primary"
              onPress={() => {
                if (selectedId) void run("add", selectedId);
              }}
            >
              {labels.add}
            </Button>
          </AppModal.Footer>
        </AppModal.Dialog>
      </AppModal.Container>
    </AppModal.Backdrop>
  );
}
